import { useEffect, useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { useChatOnboardingContext } from './ChatOnboardingController';
import { getBrazilianStatus } from '../../../utils/detectBrazil';
import { chatOnboardingPtBR } from '../../mappings/chat-onboarding.pt-br';

const ChatOnboardingStep = ({ step }) => {
  const {
    nextStep,
    previousStep,
    skipOnboarding,
    isFirstStep,
    isLastStep,
    currentStep,
    totalSteps,
    progress
  } = useChatOnboardingContext();

  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [isMobile, setIsMobile] = useState(false);
  const panelRef = useRef(null);
  const isBrazilian = getBrazilianStatus();

  // Translation function
  const t = useCallback((text) => {
    if (!isBrazilian) return text;
    return chatOnboardingPtBR[text] || text;
  }, [isBrazilian]);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    // Initial position calculation with a small delay to ensure DOM is ready
    const initialTimer = setTimeout(() => {
      try {
        // Use visualViewport for mobile to account for browser chrome
        const viewport = window.visualViewport || {
          width: window.innerWidth,
          height: window.innerHeight,
          offsetTop: 0,
          offsetLeft: 0
        };

        // For the final step, scroll to top of page
        if (step.id === 'complete') {
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
        }

        if (!step.target) {
          const viewportWidth = viewport.width;
          const viewportHeight = viewport.height;

          // For mobile, account for the actual panel width calculation
          if (isMobile) {
            // Mobile panel is w-[calc(100vw-2rem)] max-w-[260px]
            const actualPanelWidth = Math.min(viewportWidth - 32, 260);
            const panelHeight = 200; // Compact height for mobile

            setPosition({
              top: viewport.offsetTop + (viewportHeight - panelHeight) / 2,
              left: viewport.offsetLeft + (viewportWidth - actualPanelWidth) / 2,
            });
          } else {
            // Desktop panel is w-96 (384px)
            const panelWidth = 384;
            const panelHeight = 300;

            setPosition({
              top: (viewportHeight - panelHeight) / 2,
              left: (viewportWidth - panelWidth) / 2,
            });
          }
          return;
        }

        const targetElement = document.querySelector(step.target);
        if (!targetElement || !panelRef.current) {
          // Fallback to center if element not found
          const viewportWidth = viewport.width;
          const viewportHeight = viewport.height;

          if (isMobile) {
            const actualPanelWidth = Math.min(viewportWidth - 32, 260);
            const panelHeight = 200;

            setPosition({
              top: viewport.offsetTop + (viewportHeight - panelHeight) / 2,
              left: viewport.offsetLeft + (viewportWidth - actualPanelWidth) / 2,
            });
          } else {
            const panelWidth = 384;
            const panelHeight = 300;

            setPosition({
              top: (viewportHeight - panelHeight) / 2,
              left: (viewportWidth - panelWidth) / 2,
            });
          }
          return;
        }

        const targetRect = targetElement.getBoundingClientRect();
        const panelRect = panelRef.current.getBoundingClientRect();
        const viewportWidth = viewport.width;
        const viewportHeight = viewport.height;

        // Adjust spacing for mobile
        const spacing = isMobile ? 10 : 20;
        const safeAreaTop = isMobile ? 60 : 10; // Account for mobile browser UI
        const safeAreaBottom = isMobile ? 80 : 10; // Account for mobile navigation

        // Use mobile-specific position if available and on mobile
        const position = (isMobile && step.mobilePosition) ? step.mobilePosition : step.position;

        let left = targetRect.left + (targetRect.width / 2) - (panelRect.width / 2);
        let top = targetRect.bottom + spacing;

        if (position === 'top') {
          top = targetRect.top - panelRect.height - spacing;
        } else if (position === 'bottom') {
          // For mobile bottom position, place it well below the element
          if (isMobile) {
            // Place at bottom of viewport with minimal padding
            const bottomPadding = 10; // Minimal padding from bottom
            top = viewport.offsetTop + viewportHeight - panelRect.height - bottomPadding;
          } else {
            top = targetRect.bottom + spacing;
          }
        } else if (position === 'left') {
          left = targetRect.left - panelRect.width - spacing;
          top = targetRect.top + (targetRect.height / 2) - (panelRect.height / 2);
        } else if (position === 'right') {
          left = targetRect.right + spacing;
          top = targetRect.top + (targetRect.height / 2) - (panelRect.height / 2);
        }

        // Keep within safe areas - more aggressive for mobile
        left = Math.max(10, Math.min(left, viewportWidth - panelRect.width - 10));

        // For mobile bottom position, don't constrain top as much
        if (isMobile && position === 'bottom') {
          top = Math.max(top, viewport.offsetTop + safeAreaTop);
        } else {
          top = Math.max(safeAreaTop, Math.min(top + viewport.offsetTop, viewport.offsetTop + viewportHeight - panelRect.height - safeAreaBottom));
        }

        setPosition({ top, left });
      } catch (error) {
        console.warn('Chat Onboarding: Error calculating position', error);
        // Fallback to center position
        const viewport = window.visualViewport || {
          width: window.innerWidth,
          height: window.innerHeight,
          offsetTop: 0,
          offsetLeft: 0
        };

        if (isMobile) {
          const actualPanelWidth = Math.min(viewport.width - 32, 260);
          const panelHeight = 200;

          setPosition({
            top: viewport.offsetTop + (viewport.height - panelHeight) / 2,
            left: viewport.offsetLeft + (viewport.width - actualPanelWidth) / 2,
          });
        } else {
          const panelWidth = 384;
          const panelHeight = 300;

          setPosition({
            top: (viewport.height - panelHeight) / 2,
            left: (viewport.width - panelWidth) / 2,
          });
        }
      }
    }, 50);

    return () => clearTimeout(initialTimer);
  }, [step, isMobile]);

  // Different styles for mobile - much smaller and more compact
  const panelStyles = isMobile
    ? 'w-[calc(100vw-2rem)] max-w-[260px]'  // Even smaller for mobile
    : 'w-96';

  return (
    <motion.div
      ref={panelRef}
      className={`fixed ${panelStyles} bg-slate-950 border border-slate-700 shadow-2xl z-[10000]`}
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`,
      }}
      initial={false}
      animate={{
        top: position.top,
        left: position.left
      }}
      transition={{
        type: 'spring',
        stiffness: 200,
        damping: 30
      }}
    >
      <div className="relative">
        <button
          onClick={skipOnboarding}
          className={`absolute text-slate-500 hover:text-slate-300 transition-colors
            ${isMobile ? 'top-3 right-3' : 'top-4 right-4'}`}
          aria-label="Skip tour"
        >
          <X size={isMobile ? 16 : 20} />
        </button>

        <AnimatePresence mode="wait">
          <motion.div
            className={`${isMobile ? 'p-3' : 'p-6'}`}
            key={step.id}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
          <div className={`mb-1 font-mono text-slate-500 uppercase tracking-wider ${
            isMobile ? 'text-[9px]' : 'text-xs'
          }`}>
            {t('Step')} {currentStep + 1} / {totalSteps}
          </div>

          <h3 className={`font-bold text-white mb-1.5 pr-8 ${
            isMobile ? 'text-sm' : 'text-xl'
          }`}>
            {t(step.title)}
          </h3>

          <p className={`text-slate-300 leading-relaxed ${
            isMobile ? 'text-[11px] mb-3' : 'text-sm mb-6'
          }`}>
            {t(step.content)}
          </p>

          <div className={`${isMobile ? 'mb-2' : 'mb-4'}`}>
            <div className="w-full bg-slate-800 h-1 rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-gradient-to-r from-cyan-500 to-emerald-500"
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{
                  duration: 0.6,
                  ease: [0.25, 0.1, 0.25, 1]
                }}
              />
            </div>
          </div>

          <div className="flex items-center justify-between">
            <button
              onClick={previousStep}
              disabled={isFirstStep}
              className={`flex items-center space-x-1 font-medium transition-all rounded
                ${isMobile ? 'px-1.5 py-1 text-[10px]' : 'px-3 py-2 text-sm'}
                ${isFirstStep
                  ? 'text-slate-600 cursor-not-allowed'
                  : 'text-slate-300 hover:text-white hover:bg-slate-800'
                }`}
            >
              <ChevronLeft size={isMobile ? 12 : 16} />
              <span>{t('Back')}</span>
            </button>

            <div className="flex items-center space-x-1">
              {Array.from({ length: totalSteps }).map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => {}}
                  className={`${isMobile ? 'w-1 h-1' : 'w-1.5 h-1.5'} rounded-full transition-all ${
                    idx === currentStep
                      ? `bg-cyan-500 ${isMobile ? 'w-3' : 'w-4'}`
                      : idx < currentStep
                      ? 'bg-slate-600'
                      : 'bg-slate-800'
                  }`}
                />
              ))}
            </div>

            <button
              onClick={nextStep}
              className={`flex items-center space-x-1 bg-gradient-to-r from-cyan-600 to-emerald-600
                       text-white rounded font-medium hover:from-cyan-500 hover:to-emerald-500
                       transition-all transform hover:scale-105 active:scale-95
                       ${isMobile ? 'px-2 py-1 text-[10px]' : 'px-4 py-2 text-sm'}`}
            >
              <span>{isLastStep ? t('Start Chatting') : t('Next')}</span>
              <ChevronRight size={isMobile ? 12 : 16} />
            </button>
          </div>
          </motion.div>
        </AnimatePresence>

        {step.target && (
          <div
            className="absolute w-0 h-0"
            style={{
              borderStyle: 'solid',
              borderWidth: step.position === 'top' ? '0 12px 12px 12px' : '12px 12px 0 12px',
              borderColor: step.position === 'top'
                ? 'transparent transparent #1e293b transparent'
                : '#1e293b transparent transparent transparent',
              bottom: step.position === 'top' ? '-12px' : 'auto',
              top: step.position === 'bottom' ? '-12px' : 'auto',
              left: '50%',
              transform: 'translateX(-50%)',
            }}
          />
        )}
      </div>
    </motion.div>
  );
};

export default ChatOnboardingStep;